// Third-party
import React from 'react';
import { Dropdown, DropdownProps } from 'react-bootstrap';
import { DropdownItemProps } from 'react-bootstrap/DropdownItem';
import uniqueId from 'lodash/uniqueId';

// Types
import { ButtonProps } from './Button';
import { Tooltip } from '../tooltips';
import { tooltipPlacement } from '@/types/tooltip';

export type DropdownButtonProps = DropdownProps & {
  items: Array<DropdownButtonMenuItem>;
  toggle: Omit<ButtonProps, 'onClick'> & {
    component: React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement>>;
  };
  disabled?: boolean;
};

export type DropdownButtonMenuItem = Omit<DropdownItemProps<string>, 'href'> & {
  node: React.ReactNode;
  tooltip?: {
    message: string;
    place?: tooltipPlacement;
  };
  hidden?: boolean;
  icon?: string;
};

const DropdownButton: React.FC<DropdownButtonProps> = ({ children, items, toggle: { component: toggleComponent, ...toggleRest }, disabled, ...rest }) => {
  const id = uniqueId('dropdown-button-');

  return (
    <Dropdown className="dropdown-button-wrapper" {...rest}>
      <Dropdown.Toggle as={toggleComponent} id={id} {...toggleRest} disabled={disabled}>
        {children}
      </Dropdown.Toggle>
      <Dropdown.Menu as="ul" role="menu">
        {items.map(({ node, icon, hidden, tooltip, ...rest }, index) =>
          hidden ? null : (
            <li role="presentation" key={index}>
              <Tooltip place={tooltip?.place} disabled={!tooltip} message={tooltip?.message}>
                <Dropdown.Item as="button" type="button" role="menuitem" {...rest}>
                  {icon && <i className={`icon-${icon}`} />}
                  {node}
                </Dropdown.Item>
              </Tooltip>
            </li>
          ),
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default DropdownButton;
